import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import UploadIcon from '@/assets/upload-arrow-tray.svg'
import { LoadingIcon } from '@/components/LoadingIcon'
import { usePageStateStore, useProfileStore } from '@/reducers'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { PersonalInfo } from './PersonalInfo'
import { ReferralInfo } from './ReferralInfo'
import { InteracInfo } from './InteracInfo'

const toTitleCase = (str: string) =>
  str.replace(/\b\w/g, (char) => char.toUpperCase())

const toUpperCase = (str: string) => str.toUpperCase()

const capitalizeFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

export const Profile = () => {
  const { t } = useTranslation()
  const {
    setFirstName,
    setLastName,
    setEmail,
    setCity,
    setProvince,
    setPhoneNumber,
    setPostalCode,
    setBirthDay,
    setBirthMonth,
    setBirthYear,
    setAddress1,
    setAddress2,
    setInteracETransferQuestion,
    setInteracETransferAnswer,
    setReferralCode,
    setReferralCredits,
    setReferralLink,
  } = useProfileStore()
  const authToken = usePageStateStore.use.authToken() || ''
  const { data: user, isLoading } = useCurrentUser(!!authToken)

  useEffect(() => {
    if (!user) return
    setAddress1(toTitleCase(user.addressLine1 || ''))
    setAddress2(toTitleCase(user.addressLine2 || ''))
    setCity(capitalizeFirstLetter(user.city || ''))
    setProvince(toUpperCase(user.province || ''))
    setPostalCode(toUpperCase(user.postalCode || ''))
    setFirstName(toTitleCase(user.firstName || ''))
    setLastName(toTitleCase(user.lastName || ''))
    setEmail(user.email || '')
    setPhoneNumber(user.phone || '')
    setInteracETransferQuestion('Paytrie')
    setInteracETransferAnswer(user.interacAnswer || '')
    setReferralCode(user.referralCode || '')
    setReferralLink(
      `https://app.paytrie.com/SignupEmail?ref=${user.referralCode}`
    )
    setReferralCredits(user.referralCreditBalance)

    if (user.dob) {
      const [day, month, year] = user.dob.split('-')
      setBirthDay(day)
      setBirthMonth(month)
      setBirthYear(year)
    }
  }, [
    user,
    setAddress1,
    setAddress2,
    setCity,
    setProvince,
    setPostalCode,
    setFirstName,
    setLastName,
    setEmail,
    setPhoneNumber,
    setInteracETransferQuestion,
    setInteracETransferAnswer,
    setReferralCode,
    setReferralLink,
    setReferralCredits,
    setBirthDay,
    setBirthMonth,
    setBirthYear,
  ])

  if (isLoading) {
    return (
      <div
        data-testid="container-profile-loading"
        className="flex justify-center items-center min-h-screen bg-cloud dark:bg-pebble"
      >
        <LoadingIcon />
      </div>
    )
  }

  return (
    <div
      data-testid="container-profile"
      className="flex flex-col items-center justify-start min-h-screen bg-cloud dark:bg-pebble dark:text-white px-4 py-0"
    >
      <div
        data-testid="container-profile-content"
        className="flex flex-col lg:flex-row gap-8 w-full"
      >
        <div
          data-testid="container-profile-user-info"
          className="w-full lg:w-2/3 bg-cloud dark:bg-pebble p-6 rounded-lg dark:shadow-white"
        >
          <h2
            data-testid="text-profile-user-info-title"
            className="text-xl font-semibold text-left text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-900 dark:border-gray-600"
          >
            {t('profile.userInformation.title')}
          </h2>
          <div
            data-testid="container-profile-personal-info"
            className="text-left space-y-4"
          >
            <PersonalInfo />
          </div>
        </div>
        <div
          data-testid="container-profile-additional-info"
          className="w-full lg:w-2/3 bg-cloud dark:bg-pebble p-6 rounded-lg dark:shadow-white"
        >
          <h2
            data-testid="text-profile-referral-title"
            className="text-xl font-semibold text-left text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-900 dark:border-gray-600"
          >
            {t('profile.referralDetails.title')}
          </h2>
          <div
            data-testid="container-profile-referral-info"
            className="text-left space-y-4"
          >
            <ReferralInfo />
            <h2
              data-testid="text-profile-interac-title"
              className="text-xl font-semibold text-left text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-900 dark:border-gray-600"
            >
              {t('profile.interacTransfer.title')}
            </h2>
            <div data-testid="container-profile-interac-info">
              <InteracInfo />
            </div>
            <h2
              data-testid="text-profile-documents-title"
              className="text-xl font-semibold text-left text-gray-800 dark:text-gray-100 mb-4 border-b border-gray-900 dark:border-gray-600"
            >
              {t('profile.documents.title')}
            </h2>
            <Link
              to="/Upload"
              data-testid="link-profile-upload-document"
              style={{ textDecoration: 'none' }}
              className="hover:border-clementine dark:hover:border-sun flex items-center space-x-3 rounded-lg border border-gray-300 p-4 transition-colors dark:border-gray-600"
            >
              <img src={UploadIcon} alt="" className="h-7 w-7 dark:invert" />
              <div className="text-left">
                <p className="font-medium text-gray-800 dark:text-gray-100">
                  {t('profile.documents.uploadCta')}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('profile.documents.description')}
                </p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile
